import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';

/**
 * The legislative session registry — the single source for every session picker
 * and for "which session am I looking at".
 * LEGISLATIVE_SESSION_MODEL_PRD.md §5.R5.
 *
 * This replaces five separate `new Date().getFullYear()` constants, a hardcoded
 * `PRO_CURRENT_SESSION = '2026'`, and two literal year arrays that between them
 * could only ever reach two sessions of data (assessment §5). Options now come
 * from the data, so every registered session is reachable.
 *
 * `sessionConventions.test.ts` bans reintroducing any of those, which is what
 * keeps this the only definition.
 */

export interface LegislativeSession {
  sessionKey: string;
  year: number;
  type: 'regular' | 'special';
  label: string;
  shortLabel: string;
  conveneDate: string | null;
  adjournDate: string | null;
  isCurrent: boolean;
  datesConfirmed: boolean;
  counts: { bills: number; legislators: number };
}

interface SessionsResponse {
  currentSessionKey: string | null;
  sessions: LegislativeSession[];
  unresolved: { bills: number; legislators: number; calendar: number };
}

/**
 * CURRENT_SESSION is the string a filter carries to mean "whatever session is
 * current when the request runs" — resolved server-side by
 * legislativeSessionService.resolveKeys().
 *
 * It is deliberately a sentinel rather than a year. A default of
 * `getFullYear()` is stale the moment the calendar rolls over but the session has
 * not convened, which is precisely the 1 January break this work exists to fix;
 * it also bakes a date into saved views and shared URLs. The sentinel keeps the
 * default meaning "current" indefinitely, on the client and in anything that
 * persists a filter.
 */
export const CURRENT_SESSION = 'current';

export function useSessions() {
  const { data, isLoading, isError } = useQuery<SessionsResponse>(
    ['legislative-sessions'],
    async () => (await api.get('/sessions')).data.data,
    {
      // The registry changes a few times a year; match the endpoint's max-age.
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

  const sessions = data?.sessions ?? [];
  return {
    sessions,
    currentSessionKey: data?.currentSessionKey ?? null,
    currentSession: sessions.find((s) => s.isCurrent) ?? null,
    unresolved: data?.unresolved ?? null,
    isLoading,
    isError,
  };
}

/**
 * Session options for a filter control, newest first, with an "All sessions"
 * entry. Values are registry keys, so they survive a year boundary unchanged.
 */
export function useSessionOptions(includeAll = true) {
  const { sessions, isLoading } = useSessions();
  const options = sessions.map((s) => ({
    value: s.sessionKey,
    label: s.isCurrent ? `${s.shortLabel} (current)` : s.shortLabel,
    year: s.year,
  }));
  return {
    options: includeAll ? [{ value: 'all', label: 'All sessions', year: 0 }, ...options] : options,
    isLoading,
  };
}
